import { InputAdornment } from '@mui/material';
import {
  BenefitDTOBenefitEnum,
  CreateBenefitActivationDTO,
  MobilityBenefitOptionsDTOTaxationTypeEnum,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { RadioControl } from 'probonio-shared-ui/component/form';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextFieldControl } from '../../../../component/form';
import { BenefitActivationDialogExtensionInterface } from '../../../../extension/interface/benefit-activation-dialog.extension.interface';
import { ActivationDialogExtensions } from '../../ActivationDialog';

interface MobilityBenefitOptions {
  monthlyLimit: number;
  taxationType: MobilityBenefitOptionsDTOTaxationTypeEnum;
}

const InlineField: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { control } = useFormContext();

  return (
    <TextFieldControl
      slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
      rules={{
        required: true,
        min: 5,
        max: 1000,
      }}
      type="number"
      control={control}
      variant="outlined"
      name="benefitOptions.monthlyLimit"
      label={t('MOBILITY.fields.monthlyLimit')}
      margin="dense"
      fullWidth
    />
  );
};

const Fields: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { control } = useFormContext();

  return (
    <RadioControl
      control={control}
      name="benefitOptions.taxationType"
      options={[
        {
          label: t('MOBILITY.fields.taxationType.TAXABLE'),
          value: MobilityBenefitOptionsDTOTaxationTypeEnum.Taxable,
        },
        {
          label: t('MOBILITY.fields.taxationType.TAX_FREE'),
          value: MobilityBenefitOptionsDTOTaxationTypeEnum.TaxFree,
        },
      ]}
    />
  );
};

const resetMobilityBenefitOptions: ActivationDialogExtensions<MobilityBenefitOptions>['onResetBenefitOptions'] = (
  selectedActivation,
): MobilityBenefitOptions => {
  return {
    monthlyLimit: selectedActivation?.mobilityOptions ? selectedActivation.mobilityOptions.monthlyLimit / 100 : 50,
    taxationType: selectedActivation?.mobilityOptions?.taxationType || MobilityBenefitOptionsDTOTaxationTypeEnum.Taxable,
  };
};

const mapMobilityOptions = (benefitOptions: MobilityBenefitOptions) => ({
  monthlyLimit: Math.round(benefitOptions.monthlyLimit * 100),
  taxationType: benefitOptions.taxationType,
});

const createMobilityBenefitActivation: ActivationDialogExtensions<MobilityBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.Mobility,
    mobilityOptions: mapMobilityOptions(benefitOptions),
  };
  await saveActivation(create);
};

const updateMobilityBenefitActivation: ActivationDialogExtensions<MobilityBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    mobilityOptions: mapMobilityOptions(benefitOptions),
  };
  await saveActivation(update);
};

export class MobilityBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<MobilityBenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);
  InlineField?: React.ComponentType = InlineField;

  onCreateActivation = createMobilityBenefitActivation;
  onUpdateActivation = updateMobilityBenefitActivation;
  onResetBenefitOptions? = resetMobilityBenefitOptions;
}
